import { faDesktop, faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { InPersonScheduleSource, transformInPersonSchedules } from '@/utils/professional/extract-professional-location';

interface ProfessionalServiceLocationsProps {
  inPersonSchedules: InPersonScheduleSource[];
  hasTelehealth: boolean;
}

export function ProfessionalServiceLocations({
  inPersonSchedules,
  hasTelehealth,
}: ProfessionalServiceLocationsProps) {
  const inPersonLocations = transformInPersonSchedules(inPersonSchedules);

  const totalInPersonLocations = Object.values(inPersonLocations).reduce((acc, locArray) => acc + locArray.length, 0);

  const entityTitle
    = totalInPersonLocations > 1 || (hasTelehealth && totalInPersonLocations > 0)
      ? 'Locais de atendimento'
      : 'Local de atendimento';

  return (
    <div className="flex flex-col gap-4 py-4">
      <Typography variant={TypographyVariant.BODY_STRONG} as="h2" className="leading-6 sm:leading-7">
        {entityTitle}
      </Typography>

      <div className="flex flex-col gap-3">
        {hasTelehealth && (
          <div data-testid={DataTestIdEnum.PROFESSIONAL_SERVICE_LOCATIONS_TELEHEALTH_SECTION}>
            <div className="flex items-center gap-2">
              <Icon icon={faDesktop} className="text-gray-500 w-6 h-6" />
              <span className="text-gray-700 font-bold text-base">Teleconsulta</span>
            </div>
            <p className="text-gray-700 text-base pl-8">Atendimento por chamada de vídeo</p>
          </div>
        )}

        {totalInPersonLocations > 0 && (
          <div className="flex flex-col gap-3" data-testid={DataTestIdEnum.PROFESSIONAL_SERVICE_LOCATIONS_IN_PERSON_SECTION}>
            {Object.entries(inPersonLocations).map(([city, addresses]) => (
              <div key={city} data-testid={`in-person-location-${city}`}>
                <div className="flex items-center gap-2">
                  <Icon icon={faLocationDot} className="text-gray-500 w-6 h-6" />
                  <span className="text-gray-700 font-bold text-base">{city}</span>
                </div>

                <div className="flex flex-col gap-2 pl-8">
                  {addresses.map((address) => (
                    <div key={address.socialName}>
                      {address.facilitySlug
                        ? (
                            <Link
                              prefetch={false}
                              href={`/parceiros/${address.facilitySlug}/`}
                              className="text-green-500 font-semibold text-base hover:underline"
                            >
                              {formatToTitleCase(address.socialName)}
                            </Link>
                          )
                        : <p className="text-green-500 font-semibold text-base">{formatToTitleCase(address.socialName)}</p>}
                      <p className="text-gray-700 text-base">{formatToTitleCase(address.neighborhood)}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
